import { FormattedMessage } from "react-intl";
import { useInView } from "react-intersection-observer";

import { DownloadOptions } from "components/DownloadOptions/DownloadOptions";

export const DownloadsSection = () => {
	const { ref, inView } = useInView({
		triggerOnce: true,
		threshold: 0.1,
	});

	return (
		<section className="section relative overflow-hidden">
			{/* Background */}
			<div className="absolute inset-0 bg-gradient-to-br from-primary via-brand-900 to-primary" />

			{/* Geometric pattern overlay */}
			<div className="absolute inset-0 opacity-5">
				<div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-accent/20 to-transparent" />
			</div>

			{/* Accent decorations */}
			<div className="absolute top-20 left-10 w-72 h-72 bg-accent/10 rounded-full blur-3xl" />
			<div className="absolute bottom-10 right-10 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />

			<div className="container-main relative">
				<div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
					{/* Text Content */}
					<div className="text-center lg:text-left">
						{/* Badge */}
						<div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/20 border border-accent/30 rounded-full mb-6">
							<span className="relative flex h-2 w-2">
								<span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
								<span className="relative inline-flex rounded-full h-2 w-2 bg-accent"></span>
							</span>
							<span className="text-caption font-semibold text-accent uppercase tracking-wider">
								Disponible ahora
							</span>
						</div>

						<h2 className="text-display-md md:text-display-lg lg:text-display-xl text-white mb-6 leading-tight">
							<FormattedMessage id="APP.DOWNLOADS.SECTION.TITLE" />
						</h2>

						<p className="text-body-lg text-white/70 mb-8 max-w-lg mx-auto lg:mx-0 leading-relaxed">
							<FormattedMessage id="APP.DOWNLOADS.SECTION.DESCRIPTION" />
						</p>

						<DownloadOptions variant="dark" />

						{/* Trust badges */}
						<div className="flex items-center justify-center lg:justify-start gap-4 md:gap-6 mt-10 p-4 md:p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10">
							<div className="flex items-center gap-3">
								<div className="w-10 h-10 bg-yellow-500/20 rounded-xl flex items-center justify-center">
									<svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
										<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
									</svg>
								</div>
								<div>
									<div className="text-body-lg text-white font-bold">4.8</div>
									<div className="text-caption text-white/50">Rating</div>
								</div>
							</div>
							<div className="w-px h-12 bg-white/10" />
							<div className="flex items-center gap-3">
								<div className="w-10 h-10 bg-accent/20 rounded-xl flex items-center justify-center">
									<svg className="w-5 h-5 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
									</svg>
								</div>
								<div>
									<div className="text-body-lg text-white font-bold">50K+</div>
									<div className="text-caption text-white/50">Descargas</div>
								</div>
							</div>
							<div className="w-px h-12 bg-white/10 hidden sm:block" />
							<div className="hidden sm:flex items-center gap-3">
								<div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center">
									<svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
									</svg>
								</div>
								<div>
									<div className="text-body-lg text-white font-bold">100%</div>
									<div className="text-caption text-white/50">Seguro</div>
								</div>
							</div>
						</div>
					</div>

					{/* App Screenshot */}
					<div
						ref={ref}
						className={`
							relative flex justify-center lg:justify-end
							transform transition-all duration-1000 ease-out
							${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}
						`}
					>
						<div className="relative">
							{/* Glow effect behind phone */}
							<div className="absolute inset-0 bg-accent/20 rounded-3xl blur-3xl scale-90" />

							<img
								src={`${process.env.PUBLIC_URL}/assets/app_screenshots.png`}
								alt="Capturas de pantalla de la aplicación"
								className="relative w-full max-w-md lg:max-w-lg drop-shadow-2xl"
							/>

							{/* Floating badge */}
							<div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-elevated p-3 border border-surface-100 hidden md:block">
								<div className="flex items-center gap-2">
									<div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
										<svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
										</svg>
									</div>
									<span className="text-caption font-bold text-primary">Verificado</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};
